/**
 * Custom hook managing the presigned URL upload flow state machine.
 *
 * States: idle → initiating → uploading → completing → done → (idle)
 *                                                      ↳ error → (idle)
 *
 * The flow:
 * 1. POST /upload/initiate with file metadata → get presigned URLs + auditId
 * 2. PUT payslip to presigned URL (with progress tracking)
 * 3. PUT each roster file to its presigned URL (with progress tracking)
 * 4. POST /upload/complete → get auditId back
 */

import { useState, useCallback, useRef } from 'react';
import {
  initiateUpload,
  uploadFileToS3,
  completeUpload,
  ApiRequestError,
  FileMetadata,
} from '../services/api';

export type UploadState =
  | 'idle'
  | 'initiating'
  | 'uploading'
  | 'completing'
  | 'done'
  | 'error';

export interface UploadProgress {
  payslip: number; // 0-100
  roster: number;  // 0-100 (aggregate across all roster files)
  overall: number; // 0-100
}

export interface UseUploadResult {
  /** Current state of the upload flow */
  state: UploadState;
  /** Upload progress percentages */
  progress: UploadProgress;
  /** The audit ID returned after successful initiation/completion */
  auditId: string | null;
  /** User-friendly error message when in error state */
  errorMessage: string | null;
  /** Whether the error is retryable */
  isRetryable: boolean;
  /** Starts the upload flow with payslip and roster files */
  startUpload: (payslip: File, rosters: File[], retainData: boolean) => Promise<void>;
  /** Resets the hook back to idle state */
  reset: () => void;
}

function buildFileMetadata(file: File): FileMetadata {
  return {
    name: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
    // The browser cannot read a PDF's page count without a parser, so we
    // send 1 as a placeholder. The authoritative page-count check happens
    // server-side at extraction time (Textract enforces its own limits).
    pageCount: 1,
  };
}

function getUserFriendlyMessage(error: unknown): { message: string; retryable: boolean } {
  if (error instanceof ApiRequestError) {
    return { message: error.message, retryable: error.retryable };
  }
  if (error instanceof Error) {
    if (error.message.includes('network') || error.message.includes('Network')) {
      return {
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        retryable: true,
      };
    }
    return {
      message: error.message || 'An unexpected error occurred. Please try again.',
      retryable: true,
    };
  }
  return {
    message: 'An unexpected error occurred. Please try again.',
    retryable: true,
  };
}

export function useUpload(): UseUploadResult {
  const [state, setState] = useState<UploadState>('idle');
  const [progress, setProgress] = useState<UploadProgress>({
    payslip: 0,
    roster: 0,
    overall: 0,
  });
  const [auditId, setAuditId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isRetryable, setIsRetryable] = useState(false);

  // Track progress for overall calculation
  const payslipProgressRef = useRef(0);
  const rosterProgressRef = useRef<number[]>([]);

  const updateOverallProgress = useCallback(() => {
    const rosterProgresses = rosterProgressRef.current;
    const rosterAvg = rosterProgresses.length > 0
      ? rosterProgresses.reduce((sum, p) => sum + p, 0) / rosterProgresses.length
      : 0;
    const overall = Math.round(
      (payslipProgressRef.current + rosterAvg) / 2
    );
    setProgress({
      payslip: payslipProgressRef.current,
      roster: Math.round(rosterAvg),
      overall,
    });
  }, []);

  const reset = useCallback(() => {
    setState('idle');
    setProgress({ payslip: 0, roster: 0, overall: 0 });
    setAuditId(null);
    setErrorMessage(null);
    setIsRetryable(false);
    payslipProgressRef.current = 0;
    rosterProgressRef.current = [];
  }, []);

  const startUpload = useCallback(
    async (payslip: File, rosters: File[], retainData: boolean) => {
      // Reset state at the start of a new upload
      setErrorMessage(null);
      setIsRetryable(false);
      setAuditId(null);
      payslipProgressRef.current = 0;
      rosterProgressRef.current = new Array(rosters.length).fill(0);
      setProgress({ payslip: 0, roster: 0, overall: 0 });

      try {
        // Step 1: Initiate upload — get presigned URLs
        setState('initiating');

        const payslipMeta = buildFileMetadata(payslip);
        const rosterMetas = rosters.map(buildFileMetadata);

        const initResponse = await initiateUpload(payslipMeta, rosterMetas, retainData);
        setAuditId(initResponse.auditId);

        // Step 2: Upload files to S3 using presigned URLs
        setState('uploading');

        await uploadFileToS3(
          initResponse.payslipUploadUrl,
          payslip,
          (percent) => {
            payslipProgressRef.current = percent;
            updateOverallProgress();
          }
        );

        // Upload each roster file sequentially
        for (let i = 0; i < rosters.length; i++) {
          await uploadFileToS3(
            initResponse.rosterUploadUrls[i],
            rosters[i],
            (percent) => {
              rosterProgressRef.current[i] = percent;
              updateOverallProgress();
            }
          );
        }

        // Step 3: Signal upload completion
        setState('completing');

        const completeResponse = await completeUpload(initResponse.auditId);
        setAuditId(completeResponse.auditId);

        // Done
        setState('done');
        setProgress({ payslip: 100, roster: 100, overall: 100 });
      } catch (error) {
        const { message, retryable } = getUserFriendlyMessage(error);
        setState('error');
        setErrorMessage(message);
        setIsRetryable(retryable);
      }
    },
    [updateOverallProgress]
  );

  return {
    state,
    progress,
    auditId,
    errorMessage,
    isRetryable,
    startUpload,
    reset,
  };
}
